import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Camera, Image } from "lucide-react";
import { Room, normalizeTask } from "@/types/checklist";
import { useReferencePhotos } from "@/hooks/useReferencePhotos";
import { useTaskReferencePhotos } from "@/hooks/useTaskReferencePhotos";

interface ChecklistPreviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  checklistName: string;
  rooms: Room[];
  propertyId: string;
  /** When set, preview shows reference photos for this checklist. */
  checklistId?: string | null;
}

const ChecklistPreviewModal = ({
  open,
  onOpenChange,
  checklistName,
  rooms,
  propertyId,
  checklistId = null,
}: ChecklistPreviewModalProps) => {
  const { data: referencePhotosData } = useReferencePhotos(propertyId, checklistId ?? undefined);
  const { data: taskReferencePhotosData } = useTaskReferencePhotos(propertyId, checklistId ?? undefined);
  const referencePhotos = referencePhotosData?.list ?? [];
  const taskReferencePhotos = taskReferencePhotosData?.list ?? [];

  const getReferencePhotoForRoom = (roomName: string) => {
    return referencePhotos.find((p) => p.room_name === roomName);
  };

  const getTaskReferencePhoto = (roomName: string, taskText: string) => {
    return taskReferencePhotos.find(
      (p) => p.room_name.trim() === roomName.trim() && p.task_text.trim() === taskText.trim()
    );
  };

  // Filter out empty rooms/tasks for preview
  const validRooms = rooms.filter(r => r.name.trim()).map(r => ({
    ...r,
    tasks: r.tasks.map(t => normalizeTask(t)).filter(t => t.text.trim())
  }));

  const totalTasks = validRooms.reduce((sum, room) => sum + room.tasks.length, 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Cleaner View Preview</DialogTitle>
          <DialogDescription>
            This is how cleaners will see the checklist. All controls are disabled.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">{checklistName || "Untitled Checklist"}</CardTitle>
              {validRooms.length > 0 && (
                <div className="mt-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">
                      0 of {totalTasks} tasks completed (0%)
                    </span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2 mt-1">
                    <div className="h-2 rounded-full bg-primary" style={{ width: "0%" }} />
                  </div>
                </div>
              )}
            </CardHeader>
            <CardContent className="space-y-6">
              {validRooms.length === 0 ? (
                <p className="text-muted-foreground">No rooms in this checklist.</p>
              ) : (
                validRooms.map((room, roomIndex) => {
                  const roomRefPhoto = getReferencePhotoForRoom(room.name);

                  return (
                    <div key={roomIndex} className="space-y-3 pb-4 border-b last:border-b-0">
                      {/* Room header with reference photo */}
                      {roomRefPhoto ? (
                        <div className="w-full relative rounded-lg overflow-hidden">
                          <img
                            src={roomRefPhoto.photo_url}
                            alt={`${room.name} reference`}
                            className="w-full h-24 object-cover"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                          <div className="absolute bottom-2 left-3">
                            <span className="text-white font-medium text-sm">{room.name}</span>
                          </div>
                        </div>
                      ) : (
                        <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                          {room.name}
                        </h3>
                      )}

                      {/* Tasks */}
                      <div className="space-y-4 pl-2">
                        {room.tasks.map((taskObj, taskIndex) => {
                          const taskText = taskObj.text;
                          const taskRefPhoto = getTaskReferencePhoto(room.name, taskText);
                          const showRefPhoto = taskObj.require_reference_photo && taskRefPhoto;
                          const showBeforeUpload = taskObj.require_before_photo;
                          const showAfterUpload = taskObj.require_after_photo;

                          return (
                            <div key={taskIndex} className="space-y-2">
                              {/* Task checkbox (disabled) */}
                              <div className="flex items-center space-x-2">
                                <Checkbox
                                  id={`preview-task-${roomIndex}-${taskIndex}`}
                                  checked={false}
                                  disabled
                                />
                                <label
                                  htmlFor={`preview-task-${roomIndex}-${taskIndex}`}
                                  className="text-sm cursor-not-allowed flex items-center gap-2"
                                >
                                  {taskText}
                                  {(showBeforeUpload || showAfterUpload) && (
                                    <Badge
                                      variant="outline"
                                      className="text-xs px-1.5 py-0 h-5 gap-1 border-destructive text-destructive"
                                    >
                                      <Camera className="w-3 h-3" />
                                      {showBeforeUpload && showAfterUpload
                                        ? "Before + After"
                                        : showBeforeUpload
                                          ? "Before"
                                          : "After"}
                                    </Badge>
                                  )}
                                </label>
                              </div>

                              {/* Task reference photo indicator */}
                              {showRefPhoto && (
                                <div className="ml-6">
                                  <div className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-md bg-blue-50 text-blue-700 border border-blue-200">
                                    <Image className="w-3 h-3" />
                                    Reference Photo Available
                                  </div>
                                </div>
                              )}

                              {/* Before photo upload placeholder */}
                              {showBeforeUpload && (
                                <div className="ml-6">
                                  <Label className="text-xs flex items-center gap-1 text-destructive">
                                    <Camera className="w-3 h-3" />
                                    BEFORE photo (required)
                                  </Label>
                                </div>
                              )}

                              {/* After photo upload placeholder */}
                              {showAfterUpload && (
                                <div className="ml-6">
                                  <Label className="text-xs flex items-center gap-1 text-destructive">
                                    <Camera className="w-3 h-3" />
                                    AFTER photo (required)
                                  </Label>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Room reference photo display */}
                      {roomRefPhoto && (
                        <div className="pl-2 pt-2">
                          <Label className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                            <Image className="w-3 h-3" />
                            Room reference photo – match this setup
                          </Label>
                          <img
                            src={roomRefPhoto.photo_url}
                            alt={`Reference for ${room.name}`}
                            className="w-24 h-24 object-cover rounded border"
                          />
                        </div>
                      )}

                      {/* Room photo upload placeholder */}
                      <div className="pl-2 pt-2">
                        <Label className="text-xs text-muted-foreground mb-2 block">
                          Upload a cleaning photo for this room
                        </Label>
                        <div className="h-9 px-3 border border-input rounded-md bg-muted/50 flex items-center text-xs text-muted-foreground">
                          Photo upload field (disabled in preview)
                        </div>
                      </div>

                      {/* Damage selector (disabled) */}
                      <div className="pl-2 pt-2 space-y-2">
                        <div className="space-y-1">
                          <Label className="text-sm">Any damages in this room?</Label>
                          <Select value="no" disabled>
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-background">
                              <SelectItem value="no">No</SelectItem>
                              <SelectItem value="yes">Yes</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </CardContent>
          </Card>

          <p className="text-center text-xs text-muted-foreground pt-4">
            This preview is read-only. No data will be saved.
          </p>
        </div>

        <DialogFooter className="pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close Preview
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ChecklistPreviewModal;
